import { Injectable } from '@angular/core';

/**
 * Service de nettoyage du contenu HTML généré par Strapi
 * 
 * ⚠️ SOLUTION TEMPORAIRE ⚠️
 * 
 * Ce service contient des workarounds pour compenser les limitations de l'éditeur
 * de texte WYSIWYG de Strapi (CKEditor).
 * 
 * PROBLÈME ACTUEL :
 * ----------------
 * Lorsqu'un éditeur veut aligner une image à gauche SANS texte à droite (juste 
 * du texte en dessous), il doit ajouter plusieurs retours à la ligne (espaces) 
 * à droite de l'image dans l'éditeur Strapi. Ces espaces sont convertis en 
 * multiples balises <p>&nbsp;</p> dans le HTML généré, créant de grands espaces 
 * blancs indésirables dans le rendu final.
 * 
 * SOLUTION IMPLÉMENTÉE :
 * ---------------------
 * 1. Masque automatiquement tous les paragraphes vides (<p>&nbsp;</p>)
 * 2. Détecte les images avec la classe "image-style-align-left" (float: left)
 * 3. Compte les paragraphes vides consécutifs après chaque image
 * 4. Si ≥ 3 paragraphes vides → Ajoute "clear: left" sur le prochain vrai paragraphe
 *    (Cela indique que l'éditeur voulait mettre le texte en dessous, pas à côté)
 * 
 * QUAND DÉSACTIVER CE SERVICE :
 * -----------------------------
 * Ce service peut être désactivé si :
 * - L'éditeur Strapi est mis à jour avec une meilleure gestion des images flottantes
 * - Strapi permet de choisir entre "texte à côté" vs "texte en dessous" pour les images
 * - Un plugin Strapi est installé pour gérer ce cas d'usage
 * - L'équipe éditoriale n'utilise plus d'images alignées à gauche/droite
 * 
 * Pour désactiver :
 * - Supprimer les appels à cleanStrapiContent() dans les composants
 * - Ou ajouter un flag de configuration et vérifier avant d'exécuter
 * 
 * @date 2025-11-07
 * @temporary true
 */
@Injectable({
  providedIn: 'root'
})
export class StrapiContentCleanerService {

  constructor() { }

  /**
   * Nettoie le contenu HTML d'un conteneur en masquant les paragraphes vides
   * et en ajoutant des clearfix automatiques pour les images flottantes
   * 
   * @param containerElement L'élément DOM contenant le contenu HTML à nettoyer
   * @returns true si des modifications ont été effectuées, false sinon
   * 
   * @example
   * ```typescript
   * ngAfterViewChecked() {
   *   const container = this.elementRef.nativeElement.querySelector('.content-container');
   *   if (container) {
   *     this.strapiCleaner.cleanStrapiContent(container);
   *   }
   * }
   * ```
   */
  public cleanStrapiContent(containerElement: HTMLElement): boolean {
    if (!containerElement) {
      console.warn('[StrapiContentCleanerService] Container element is null or undefined');
      return false;
    }

    const allElements = Array.from(containerElement.querySelectorAll('p, figure')) as Element[];
    let hasCleanedSomething = false;
    let consecutiveEmptyCount = 0;
    let lastFloatingImage: Element | null = null;

    allElements.forEach((element: Element) => {
      // Détecter les images flottantes à gauche ou à droite
      if (element.tagName === 'FIGURE' && 
          (element.classList.contains('image-style-align-left') ||
           element.classList.contains('image-style-align-right'))) {
        lastFloatingImage = element;
        consecutiveEmptyCount = 0;
        return;
      }

      // Vérifier si c'est un paragraphe
      if (element.tagName === 'P') {
        const p = element as HTMLParagraphElement;
        
        if (this.isEmptyParagraph(p)) {
          // C'est un paragraphe vide, on le masque
          p.style.display = 'none';
          hasCleanedSomething = true;
          
          // Si on est après une image flottante, compter les paragraphes vides consécutifs
          if (lastFloatingImage) {
            consecutiveEmptyCount++;
          }
        } else {
          // C'est un vrai paragraphe avec du contenu
          // Si on a eu beaucoup de paragraphes vides avant (≥3), c'est un indicateur
          // que l'éditeur voulait mettre ce texte en dessous de l'image
          if (lastFloatingImage && consecutiveEmptyCount >= 3) {
            p.style.clear = lastFloatingImage.classList.contains('image-style-align-left') 
              ? 'left' 
              : 'right';
          }
          
          // Reset
          consecutiveEmptyCount = 0;
          lastFloatingImage = null;
        }
      } else {
        // Autre élément (pas un <p> ni une <figure>), reset
        consecutiveEmptyCount = 0;
        lastFloatingImage = null;
      }
    });

    return hasCleanedSomething;
  }

  /**
   * Vérifie si un paragraphe est considéré comme "vide"
   * Un paragraphe vide contient uniquement :
   * - Des espaces blancs
   * - Des espaces insécables (&nbsp;)
   * - Éventuellement une balise <br>
   * 
   * @param paragraph L'élément <p> à vérifier
   * @returns true si le paragraphe est vide, false sinon
   */
  private isEmptyParagraph(paragraph: HTMLParagraphElement): boolean {
    const text = paragraph.textContent?.trim() || '';
    const hasOnlyNbsp = text === '' || text === '\u00A0' || /^[\s\u00A0]+$/.test(text);
    const hasNoChildren = paragraph.children.length === 0 || 
                         (paragraph.children.length === 1 && paragraph.children[0].tagName === 'BR');
    
    return hasOnlyNbsp && hasNoChildren;
  }

  /**
   * Réinitialise les modifications appliquées par cleanStrapiContent()
   * Utile pour le développement/debug ou si on veut voir le contenu original
   * 
   * @param containerElement L'élément DOM dont on veut restaurer le contenu
   */
  public resetCleanedContent(containerElement: HTMLElement): void {
    if (!containerElement) return;

    const paragraphs = containerElement.querySelectorAll('p');
    paragraphs.forEach((p: HTMLParagraphElement) => {
      p.style.display = '';
      p.style.clear = '';
    });
  }
}
